<template>
  <div class="page-card">
    <div class="page-head">
      <h3>Device Models</h3>
      <el-button type="primary" size="small" @click="openCreate">New Device Model</el-button>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="Keyword">
        <el-input v-model="query.keyword" clearable placeholder="Name / brand / code" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item label="Status">
        <el-select v-model="query.status" clearable placeholder="All">
          <el-option label="enabled" value="enabled" />
          <el-option label="disabled" value="disabled" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">Search</el-button>
        <el-button @click="resetSearch">Reset</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" v-loading="loading" border size="small">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column prop="modelName" label="Model Name" min-width="180" show-overflow-tooltip />
      <el-table-column prop="brand" label="Brand" width="140" show-overflow-tooltip />
      <el-table-column prop="modelCode" label="Code" width="150" show-overflow-tooltip />
      <el-table-column prop="status" label="Status" width="110">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 'enabled' ? 'success' : 'info'" size="small">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="sortOrder" label="Sort" width="80" />
      <el-table-column prop="remark" label="Remark" min-width="220" show-overflow-tooltip />
      <el-table-column prop="updateTime" label="Updated At" min-width="160" />
      <el-table-column label="Actions" width="150" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">Edit</el-button>
          <el-button type="text" size="small" class="danger" @click="remove(scope.row)">Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-size="query.pageSize"
      :current-page="query.pageNum"
      :page-sizes="[10, 20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="560px">
      <el-form :model="form" label-width="110px" size="small">
        <el-form-item label="Model Name">
          <el-input v-model="form.modelName" placeholder="Apple Watch Ultra" />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="Brand">
              <el-input v-model="form.brand" placeholder="Apple" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Code">
              <el-input v-model="form.modelCode" placeholder="watch-ultra" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Status">
              <el-select v-model="form.status" style="width:100%">
                <el-option label="enabled" value="enabled" />
                <el-option label="disabled" value="disabled" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Sort">
              <el-input-number v-model="form.sortOrder" :min="0" style="width:100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="Remark">
          <el-input v-model="form.remark" type="textarea" :rows="3" placeholder="Optional admin note." />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible=false">Cancel</el-button>
        <el-button type="primary" @click="submit">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { deviceModelApi } from '@/api/system'

const emptyQuery = () => ({ pageNum: 1, pageSize: 10, keyword: '', status: '' })

const emptyForm = () => ({
  id: null,
  modelName: '',
  brand: '',
  modelCode: '',
  status: 'enabled',
  sortOrder: 0,
  remark: ''
})

export default {
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: emptyQuery(),
      dialogVisible: false,
      dialogTitle: 'New Device Model',
      form: emptyForm()
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await deviceModelApi.list(this.query)
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = emptyQuery()
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    openCreate() {
      this.dialogTitle = 'New Device Model'
      this.form = emptyForm()
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = 'Edit Device Model'
      this.form = { ...emptyForm(), ...row }
      this.dialogVisible = true
    },
    async submit() {
      if (!this.form.modelName) {
        this.$message.warning('Please enter a model name')
        return
      }
      if (this.form.id) await deviceModelApi.update(this.form)
      else await deviceModelApi.create(this.form)
      this.$message.success('Saved successfully')
      this.dialogVisible = false
      this.loadData()
    },
    async remove(row) {
      await this.$confirm(`Delete device model "${row.modelName}"?`, 'Confirm', { type: 'warning' })
      await deviceModelApi.remove(row.id)
      this.$message.success('Deleted successfully')
      this.loadData()
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.pager { margin-top: 16px; text-align: right; }
.danger { color: #f56c6c; }
</style>
